import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    AccessTime,
    Groups,
    SettingsSuggest,
    EmojiObjects,
    CheckCircle,
    ChatBubbleOutline,
    Nightlight,
    WbSunny,
    Rule,
    MenuBook
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアでの夜勤・シフト勤務の悩みとは
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('rhythm-adjustment')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    夜勤・シフト勤務と生活リズムの調整ポイント
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('rule-tips')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    快適なルームシェア生活のためのルール作りのコツ
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('experience')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    体験談＆具体的な成功事例
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('summary')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    まとめ＆明日からできるアクション
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Groups sx={{ mr: 1, verticalAlign: 'middle' }} />
                    ルームシェアでの夜勤・シフト勤務の悩みとは
                </Typography>
                <Typography variant="body1" paragraph>
                    「ルームシェアに憧れるけど、夜勤やシフト勤務だと難しいのでは？」そんな疑問や不安を抱えていませんか？<br />
                    実は、夜勤や不規則なシフト勤務をしながらでも、ルームシェアで快適に暮らしている人はたくさんいます。<br />
                    しかし、生活リズムの違いによる摩擦や、眠りの妨げ、共用スペースの使い方など、シフト勤務特有の悩みはつきもの。そこで本記事では、
                    <Chip label="夜勤・シフト勤務" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />
                    をしながらでも快適にルームシェア生活を送るための実践的なアイデアや工夫、体験談をたっぷりご紹介します。
                </Typography>
                <Box sx={{ textAlign: 'center', my: 3 }}>
                    <img
                        src="/images/blog/nightshift-cozy-living_roomshare-nightshift-tips.jpg"
                        alt="夜勤明けの住人が静かな朝の共有リビングでリラックスし、他のルームメイトはまだ就寝中。柔らかな青白い光と落ち着いた雰囲気が、生活リズムの違いにも配慮した空間を象徴している。"
                        style={{ maxWidth: '100%', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 生活リズムの調整ポイント */}
            <Box id="rhythm-adjustment" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
                    夜勤・シフト勤務と生活リズムの調整ポイント
                </Typography>
                <Typography variant="body1" paragraph>
                    夜勤や不規則シフトのルームメイトがいる環境では、お互いの生活リズムを理解し合うことが大切です。ここでは、生活リズムを快適に保つための具体的な対策を解説します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Nightlight sx={{ color: '#3949ab', mr: 1 }} />
                                <Typography variant="h6" component="h3">夜勤の人のための静音工夫</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="イヤーマフや耳栓・アイマスクで睡眠環境を整える" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="寝室のドアに「就寝中」などのサインを設置" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="防音カーテンや厚手のカーテンを活用" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <WbSunny sx={{ color: '#fbc02d', mr: 1 }} />
                                <Typography variant="h6" component="h3">日中活動派の配慮ポイント</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="早朝・午前中の掃除や洗濯は控えめに" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="共有スペースでの会話は控えめな声量で" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircle color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="家電やドアの開閉は静かに行う" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/sleeping-arrangement_roomshare-nightshift-tips.jpg"
                        alt="遮光カーテンと耳栓を使い、昼間でも快適に眠る夜勤ルームメイトの寝室。静音家電や気遣いのメモも見える、共同生活の思いやりを映したワンシーン。"
                        style={{ maxWidth: '100%', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    生活リズムが異なるからこそ、お互いの「当たり前」が通じないことも。最初にしっかり話し合うことで、トラブルの防止につながります。
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ルール作りのコツ */}
            <Box id="rule-tips" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Rule sx={{ mr: 1, verticalAlign: 'middle' }} />
                    快適なルームシェア生活のためのルール作りのコツ
                </Typography>
                <Typography variant="body1" paragraph>
                    夜勤・シフト勤務でも安心して暮らすためには、明確なルールや合意形成が欠かせません。ポイントを押さえたルール作りで、ストレスや不満をぐっと減らしましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <SettingsSuggest color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            1. 生活リズム「見える化」ルール
                                        </Typography>
                                    }
                                    secondary="各自の主な勤務パターンや就寝時間をホワイトボードやアプリで共有。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <ChatBubbleOutline color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            2. 配慮のお願い・NG行動の明文化
                                        </Typography>
                                    }
                                    secondary="「〇時〜〇時は静かに」「来客時は事前連絡」など具体的なルールを決めておく。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <EmojiObjects color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            3. 定期的なルール見直し
                                        </Typography>
                                    }
                                    secondary="月1回程度のミーティングで不満や改善点を話し合い、柔軟にルールを更新。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <MenuBook color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            4. 休日や特別な日の過ごし方
                                        </Typography>
                                    }
                                    secondary="休日やイベント時の「早起き・夜更かし」など、普段と違うスケジュールを事前共有。"
                                />
                            </ListItem>
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Chip color="primary" label="トラブル防止" variant="outlined" sx={{ mr: 1 }} />
                            <Chip color="primary" label="ストレス軽減" variant="outlined" />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: '100%' }}>
                            <img
                                src="/images/blog/rule-board_roomshare-nightshift-tips.jpg"
                                alt="共有スペースに設置されたホワイトボードに、シフトスケジュールやルールがカラフルに記入され、全員が確認できる工夫がされている。"
                                style={{ maxWidth: '100%', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 体験談＆具体的な成功事例 */}
            <Box id="experience" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <MenuBook sx={{ mr: 1, verticalAlign: 'middle' }} />
                    体験談＆具体的な成功事例
                </Typography>
                <Typography variant="body1" paragraph>
                    実際に夜勤・シフト勤務でルームシェアをしている方の声や、うまくいった工夫を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar src="/images/avatars/roommate-1.jpg" alt="30代女性のルームメイト・看護師" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">看護師・Aさん</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「夜勤明けで昼間に寝るため、最初は生活音が気になって辛かったです。でも、共用部の掃除や洗濯の時間を調整してもらい、防音カーテンやアイマスクを使うようにしたら、驚くほど眠れるようになりました！」
                                </Typography>
                                <Chip label="夜勤×ルームシェア成功" size="small" color="primary" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar src="/images/avatars/roommate-2.jpg" alt="20代男性のルームメイト・ITエンジニア" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">ITエンジニア・Bさん</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「自分は夜型でリモートワーク、同居人は日中勤務。互いに“静かタイム”を設けたり、チャットで連絡を取り合うことで、お互いストレスなく過ごせています。ルールは柔軟に変えていくのがコツ！」
                                </Typography>
                                <Chip label="柔軟なルール運用" size="small" color="primary" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        体験者の多くが「最初の話し合い」と「定期的なルール見直し」の重要性を実感しています。シフト勤務でも、お互いに気持ち良く暮らせる工夫は必ず見つかります。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* まとめ＆明日からできるアクション */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <EmojiObjects sx={{ mr: 1, verticalAlign: 'middle' }} />
                    まとめ＆明日からできるアクション
                </Typography>
                <Typography variant="body1" gutterBottom>
                    夜勤やシフト勤務でも、ルームシェアはしっかり工夫すれば快適なものになります。最後に、すぐ取り入れられるアクションポイントをまとめました。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="自分の生活リズム・勤務時間をルームメイトに伝える" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="配慮してほしい点を具体的にリストアップして話し合う" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="就寝サインや静音グッズなど実践的な工夫を導入" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="定期的なミーティングでルールを見直す" />
                    </ListItem>
                </List>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/cozy-night-morning_roomshare-nightshift-tips.jpg"
                        alt="朝と夜が交錯する共有リビングで、夜勤明けの住人と日勤前の住人がすれ違いながらも優しく挨拶を交わす、思いやりのあるルームシェアの雰囲気。"
                        style={{ maxWidth: '100%', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                    お互いの違いを楽しみながら、思いやりを持って暮らす。それこそが、夜勤・シフト勤務でも幸せなルームシェア生活の秘訣です。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-nightshift-tips',
    slug: 'roomshare-nightshift-tips',
    title: 'ルームシェアで「夜勤・シフト勤務」でも快適に暮らす方法',
    excerpt: '夜勤・シフト勤務者がルームシェアで抱える悩みや生活リズムの調整方法、ルール作りのコツ、体験談までを専門的に解説します。',
    coverImage: '/images/blog/cover-roomshare-nightshift_roomshare-nightshift-tips.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。生活リズムの違いを受け入れ合う和やかな雰囲気と、仲間同士の信頼感が伝わるシーン。',
    publishedAt: '2024-06-09T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', 'ライフスタイル', '夜勤・シフト勤務'],
    tags: ['夜勤', 'シフト勤務', 'ルームシェア', '生活リズム', 'ルール作り'],
    author: {
        name: '仲井 理恵',
        avatar: '/images/avatars/rie-nakai.jpg',
        bio: '都内で10年以上シェアハウス運営に携わり、多様なライフスタイルと快適な共同生活の工夫を提案。ルームシェア・コンサル経験も豊富。',
        profilePicture: '/images/avatars/rie-nakai.jpg'
    },
    viewCount: 1573,
    contentComponent: PostContent
};

export default post;